export type FocusDirection = 'up' | 'down' | 'left' | 'right';
export type FocusRole = 'rail' | 'hero' | 'card' | 'card-action' | 'tab' | 'input' | 'menu-item' | 'transport' | 'slider' | 'control';
export type FeedbackCue = 'focus-item' | 'focus-group' | 'activate' | 'back' | 'boundary' | 'success' | 'error';

export type FocusRect = { left: number; top: number; width: number; height: number };
export type FocusDestinations = Partial<Record<FocusDirection, string>>;

export type FocusNodeSnapshot = {
  id: string;
  group: string;
  role: FocusRole;
  rect: FocusRect;
  order: number;
  destinations?: FocusDestinations;
  disabled?: boolean;
  scope?: string;
};

export type FocusDecision = {
  destinationId: string | null;
  cue: Extract<FeedbackCue, 'focus-item' | 'focus-group' | 'boundary'>;
  reason: 'explicit' | 'same-group' | 'adjacent-group' | 'rail' | 'boundary' | 'initial';
  candidates: Array<{ id: string; score: number }>;
};

const center = (rect: FocusRect) => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
});

function directionalScore(source: FocusNodeSnapshot, candidate: FocusNodeSnapshot, direction: FocusDirection) {
  const from = center(source.rect);
  const to = center(candidate.rect);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const primary = direction === 'left' ? -dx : direction === 'right' ? dx : direction === 'up' ? -dy : dy;
  if (primary <= 4) return null;
  const perpendicular = direction === 'left' || direction === 'right' ? Math.abs(dy) : Math.abs(dx);
  const sourceSpan = direction === 'left' || direction === 'right' ? source.rect.height : source.rect.width;
  const candidateSpan = direction === 'left' || direction === 'right' ? candidate.rect.height : candidate.rect.width;
  const alignmentAllowance = Math.max(sourceSpan, candidateSpan) * 0.55;
  const alignmentPenalty = Math.max(0, perpendicular - alignmentAllowance) * 2.5;
  const groupPenalty = source.group === candidate.group ? 0 : 180;
  return primary + perpendicular * 1.35 + alignmentPenalty + groupPenalty + candidate.order * 0.0001;
}

export function resolveFocusMove(
  nodes: FocusNodeSnapshot[],
  activeId: string | null,
  direction: FocusDirection,
  preferredId?: string | null,
): FocusDecision {
  const eligible = nodes.filter((node) => !node.disabled && node.rect.width > 0 && node.rect.height > 0);
  if (!eligible.length) return { destinationId: null, cue: 'boundary', reason: 'boundary', candidates: [] };
  const active = eligible.find((node) => node.id === activeId);
  if (!active) {
    const preferred = eligible.find((node) => node.id === preferredId)
      ?? eligible.find((node) => node.role === 'hero')
      ?? eligible.find((node) => node.role === 'rail')
      ?? [...eligible].sort((a, b) => a.order - b.order)[0];
    return { destinationId: preferred.id, cue: 'focus-group', reason: 'initial', candidates: [] };
  }

  const explicitId = active.destinations?.[direction];
  if (explicitId) {
    const explicit = eligible.find((node) => node.id === explicitId);
    if (explicit) {
      return {
        destinationId: explicit.id,
        cue: explicit.group === active.group ? 'focus-item' : 'focus-group',
        reason: 'explicit',
        candidates: [{ id: explicit.id, score: -1 }],
      };
    }
  }

  // The profile/cast/search controls live in the top utility band. There is
  // no meaningful content above that band, so Up must stop instead of using
  // page geometry to jump into whichever shelf happens to be visible.
  if (active.group === 'shell.utility' && direction === 'up') {
    return { destinationId: null, cue: 'boundary', reason: 'boundary', candidates: [] };
  }

  const scored = eligible
    // The rail is a horizontal escape destination from content, never a
    // vertical destination from a hero/card. Rail controls may still move
    // vertically among themselves when the rail is already active.
    .filter((node) => !(node.role === 'rail' && active.role !== 'rail' && (direction === 'up' || direction === 'down')))
    // A side rail is its own vertical navigation column. Content and utility
    // controls may be entered horizontally, never by stepping vertically
    // out of a rail item.
    .filter((node) => !(active.role === 'rail' && node.role !== 'rail' && (direction === 'up' || direction === 'down')))
    // Utility controls belong to the shell edge, not the content column. They
    // are reachable through explicit rail/utility destinations only; their
    // screen coordinates must not steal a content row's vertical move.
    .filter((node) => !(node.group === 'shell.utility' && active.role !== 'rail' && (direction === 'up' || direction === 'down')))
    // Card actions belong to the pointer/hover layer. They may only enter the
    // D-pad graph when the current node explicitly names one as a destination;
    // otherwise their overlapping rectangles can steal vertical row changes.
    .filter((node) => !(node.role === 'card-action' && !active.destinations?.[direction]))
    .filter((node) => node.id !== active.id)
    .map((node) => ({ node, score: directionalScore(active, node, direction) }))
    .filter((entry): entry is { node: FocusNodeSnapshot; score: number } => entry.score !== null)
    .sort((a, b) => a.score - b.score || a.node.order - b.node.order);
  const horizontalCardMove = active.role === 'card' && (direction === 'left' || direction === 'right');
  const scoped = horizontalCardMove ? scored.filter(({ node }) => node.group === active.group) : scored;
  const best = scoped[0]?.node;
  if (best) {
    return {
      destinationId: best.id,
      cue: best.group === active.group ? 'focus-item' : 'focus-group',
      reason: best.group === active.group ? 'same-group' : 'adjacent-group',
      candidates: scoped.slice(0, 8).map(({ node, score }) => ({ id: node.id, score })),
    };
  }

  if (direction === 'left' && active.role !== 'rail') {
    const source = center(active.rect);
    const rail = eligible
      .filter((node) => node.role === 'rail')
      .map((node) => ({ node, score: Math.abs(center(node.rect).y - source.y) }))
      .sort((a, b) => a.score - b.score)[0];
    if (rail) return { destinationId: rail.node.id, cue: 'focus-group', reason: 'rail', candidates: [{ id: rail.node.id, score: rail.score }] };
  }

  return { destinationId: null, cue: 'boundary', reason: 'boundary', candidates: [] };
}
